(() => {
  const SELECTOR = '[data-custom-popup-banner]';
  const BODY_LOCK_CLASS = 'custom-popup-banner-lock';
  const MOBILE_BREAKPOINT = 749;

  function initPopup(root) {
    if (!root || root.dataset.popupInitialized === 'true') return;

    root.dataset.popupInitialized = 'true';

    const overlay = root.querySelector('[data-popup-overlay]');
    const closeButton = root.querySelector('[data-popup-close]');

    const sectionId = root.dataset.sectionId || 'global';

    const trigger = root.dataset.trigger || 'delay';
    const delaySeconds = Number(root.dataset.delay || 0);

    const deviceTarget = root.dataset.device || 'all';

    const showOnlyOnce = root.dataset.showOnce === 'true';
    const showAgainDays = Number(root.dataset.showAgainDays || 0);

    const designMode = root.dataset.designMode === 'true';

    const storageKey = `bubs-popup-dismissed-${sectionId}`;

    let opened = false;
    let triggerTimer = null;
    let exitIntentHandler = null;

    function isMobile() {
      return window.matchMedia(
        `(max-width: ${MOBILE_BREAKPOINT}px)`
      ).matches;
    }

    function deviceAllowed() {
      const mobile = isMobile();

      if (deviceTarget === 'mobile' && !mobile) {
        return false;
      }

      if (deviceTarget === 'desktop' && mobile) {
        return false;
      }

      return true;
    }

    function getStoredDismissal() {
      if (!showOnlyOnce || designMode) {
        return null;
      }

      try {
        const raw = localStorage.getItem(storageKey);

        if (!raw) return null;

        const parsed = JSON.parse(raw);

        if (
          !parsed ||
          typeof parsed.dismissedAt !== 'number'
        ) {
          return null;
        }

        return parsed.dismissedAt;
      } catch (error) {
        /*
         * localStorage may be blocked by the browser,
         * privacy mode or another security restriction.
         *
         * In that case the popup should continue working.
         */
        return null;
      }
    }

    function shouldSuppressPopup() {
      if (!showOnlyOnce || designMode) {
        return false;
      }

      const dismissedAt = getStoredDismissal();

      if (!dismissedAt) {
        return false;
      }

      /*
       * 0 means never show again after dismissal.
       */
      if (showAgainDays === 0) {
        return true;
      }

      const millisecondsPerDay = 24 * 60 * 60 * 1000;

      const elapsed =
        Date.now() - dismissedAt;

      const requiredDelay =
        showAgainDays * millisecondsPerDay;

      return elapsed < requiredDelay;
    }

    function saveDismissal() {
      if (!showOnlyOnce || designMode) {
        return;
      }

      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            dismissedAt: Date.now()
          })
        );
      } catch (error) {
        /*
         * Fail silently.
         * Popup functionality should never depend on
         * localStorage being available.
         */
      }
    }

    function lockPageScroll() {
      document.documentElement.classList.add(
        BODY_LOCK_CLASS
      );

      document.body.classList.add(
        BODY_LOCK_CLASS
      );
    }

    function unlockPageScroll() {
      document.documentElement.classList.remove(
        BODY_LOCK_CLASS
      );

      document.body.classList.remove(
        BODY_LOCK_CLASS
      );
    }

    function openPopup() {
      if (opened) return;
      if (!deviceAllowed()) return;
      if (shouldSuppressPopup()) return;

      opened = true;

      root.hidden = false;

      /*
       * Force one rendering frame before adding
       * the animation class.
       */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.add('is-open');

          lockPageScroll();

          if (closeButton) {
            closeButton.focus({
              preventScroll: true
            });
          }
        });
      });
    }

    function closePopup({
      remember = true
    } = {}) {
      if (!opened) return;

      opened = false;

      root.classList.remove('is-open');

      unlockPageScroll();

      if (remember) {
        saveDismissal();
      }

      window.setTimeout(() => {
        if (!opened) {
          root.hidden = true;
        }
      }, 350);
    }

    function handleEscape(event) {
      if (
        event.key === 'Escape' &&
        opened
      ) {
        closePopup();
      }
    }

    function setupImmediateTrigger() {
      openPopup();
    }

    function setupDelayTrigger() {
      triggerTimer = window.setTimeout(
        openPopup,
        Math.max(0, delaySeconds) * 1000
      );
    }

    function setupExitIntentTrigger() {
      /*
       * Standard desktop exit intent:
       * trigger when the pointer leaves the top of
       * the browser viewport.
       */
      if (!isMobile()) {
        exitIntentHandler = (event) => {
          if (opened) return;

          if (
            event.clientY <= 5 &&
            !event.relatedTarget
          ) {
            openPopup();

            document.removeEventListener(
              'mouseout',
              exitIntentHandler
            );
          }
        };

        document.addEventListener(
          'mouseout',
          exitIntentHandler
        );

        return;
      }

      /*
       * True cursor-based exit intent does not exist
       * on touch devices.
       *
       * For mobile, detect a deliberate upward swipe
       * while the visitor is close to the top of the
       * page. This provides a lightweight touch-device
       * equivalent without interfering with browser
       * navigation.
       */
      let touchStartY = null;
      let touchStartScrollY = null;

      const handleTouchStart = (event) => {
        if (!event.touches?.length) return;

        touchStartY = event.touches[0].clientY;
        touchStartScrollY = window.scrollY;
      };

      const handleTouchEnd = (event) => {
        if (
          opened ||
          touchStartY === null ||
          touchStartScrollY === null
        ) {
          return;
        }

        const touch =
          event.changedTouches &&
          event.changedTouches[0];

        if (!touch) return;

        const movement =
          touch.clientY - touchStartY;

        /*
         * Downward finger movement while already close
         * to the top often indicates intent to leave
         * or return to the browser chrome.
         */
        if (
          touchStartScrollY < 120 &&
          movement > 70
        ) {
          openPopup();

          document.removeEventListener(
            'touchstart',
            handleTouchStart
          );

          document.removeEventListener(
            'touchend',
            handleTouchEnd
          );
        }

        touchStartY = null;
        touchStartScrollY = null;
      };

      document.addEventListener(
        'touchstart',
        handleTouchStart,
        {
          passive: true
        }
      );

      document.addEventListener(
        'touchend',
        handleTouchEnd,
        {
          passive: true
        }
      );
    }

    function setupTrigger() {
      if (!deviceAllowed()) {
        root.hidden = true;
        return;
      }

      if (shouldSuppressPopup()) {
        root.hidden = true;
        return;
      }

      switch (trigger) {
        case 'immediate':
          setupImmediateTrigger();
          break;

        case 'exit':
          setupExitIntentTrigger();
          break;

        case 'delay':
        default:
          setupDelayTrigger();
          break;
      }
    }

    if (overlay) {
      overlay.addEventListener(
        'click',
        () => closePopup()
      );
    }

    if (closeButton) {
      closeButton.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          closePopup();
        }
      );
    }

    /*
     * Clicking inside the actual modal should never
     * trigger the overlay close action.
     */
    const dialog = root.querySelector(
      '.custom-popup-banner__dialog'
    );

    if (dialog) {
      dialog.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
        }
      );
    }

    document.addEventListener(
      'keydown',
      handleEscape
    );

    setupTrigger();
  }

  function initAllPopups(container = document) {
    container
      .querySelectorAll(SELECTOR)
      .forEach(initPopup);
  }

  function boot() {
    initAllPopups();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  /*
   * Shopify theme editor support.
   *
   * If this section is dynamically added/reloaded
   * while customising the theme, initialise it again.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (!event.target) return;

      if (
        event.target.matches?.(SELECTOR)
      ) {
        initPopup(event.target);
        return;
      }

      initAllPopups(event.target);
    }
  );
})();